/**
 * ConfirmApplyModal — organism (CES standard)
 * Confirmation step before applying a capacity task: summary of the change
 * (scope + diff), TaskActionOnBlockingInstances choice, and optional
 * InstancesToExclude. Exclusions are picked from the scope's real running
 * instances (checkboxes — no free text, mirroring the AWS console but with
 * resolved names), plus derived account/service groups.
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import type { InstancePoolDiffEntry } from 'slotting-core';
import type {
  InstanceSummary,
  InstancesToExclude,
  TaskActionOnBlockingInstances,
} from '../../types/outposts';
import { Button } from '../atoms/Button';
import { DiffTable } from '../molecules/DiffTable';

export interface ApplyOptions {
  taskAction: TaskActionOnBlockingInstances;
  instancesToExclude?: InstancesToExclude;
}

function toggleValue(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

export function ConfirmApplyModal({
  scopeLabel,
  diff,
  pending,
  instances,
  instancesLoading = false,
  massive = false,
  onConfirm,
  onCancel,
}: {
  scopeLabel: string;
  diff: InstancePoolDiffEntry[];
  pending: boolean;
  /** Running instances in the scope; null when the lookup failed. */
  instances: InstanceSummary[] | null;
  instancesLoading?: boolean;
  /**
   * Outpost-wide change: adds a final warning step — AWS decides placement
   * across the whole droplet farm, so the apply needs explicit acknowledgment.
   */
  massive?: boolean;
  onConfirm: (options: ApplyOptions) => void;
  onCancel: () => void;
}) {
  const [taskAction, setTaskAction] = useState<TaskActionOnBlockingInstances>('FAIL_TASK');
  const [excludedInstances, setExcludedInstances] = useState<string[]>([]);
  const [excludedAccounts, setExcludedAccounts] = useState<string[]>([]);
  const [excludedServices, setExcludedServices] = useState<string[]>([]);
  const [step, setStep] = useState<'form' | 'warning'>('form');
  const [acknowledged, setAcknowledged] = useState(false);

  // Escape closes (client Modal behavior); ignored while an apply is pending
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pending) onCancel();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [pending, onCancel]);

  // Account/service exclusion groups derived from what actually runs here
  const accounts = useMemo(
    () => [...new Set((instances ?? []).map((i) => i.accountId))].sort(),
    [instances],
  );
  const services = useMemo(
    () => [...new Set((instances ?? []).map((i) => i.awsServiceName))].sort(),
    [instances],
  );

  const buildOptions = (): ApplyOptions => {
    const instancesToExclude: InstancesToExclude | undefined =
      excludedInstances.length > 0 || excludedAccounts.length > 0 || excludedServices.length > 0
        ? {
            ...(excludedInstances.length > 0 ? { instances: excludedInstances } : {}),
            ...(excludedAccounts.length > 0 ? { accountIds: excludedAccounts } : {}),
            ...(excludedServices.length > 0 ? { services: excludedServices } : {}),
          }
        : undefined;
    return { taskAction, instancesToExclude };
  };

  const confirm = () => {
    // Outpost-wide changes require the explicit final acknowledgment step
    if (massive && step === 'form') {
      setStep('warning');
      return;
    }
    onConfirm(buildOptions());
  };

  const protectedCount =
    excludedInstances.length + excludedAccounts.length + excludedServices.length;

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black opacity-40"
        role="presentation"
        onClick={pending ? undefined : onCancel}
      />

      {/* Modal container */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4">
        <div
          className="pointer-events-auto relative w-full max-w-2xl rounded-lg border border-gray-200 bg-white shadow-xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="apply-modal-title"
          data-testid="apply-modal"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex-1">
              <h3 id="apply-modal-title" className="text-lg font-semibold text-gray-900">
                Apply capacity change
              </h3>
              <p className="mt-1 text-sm text-gray-600">{scopeLabel}</p>
            </div>
            <button
              type="button"
              onClick={pending ? undefined : onCancel}
              className="ml-4 flex-shrink-0 rounded p-1 text-gray-400 transition-colors duration-200 hover:bg-gray-200 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content — final warning step (outpost-wide changes only) */}
          {step === 'warning' ? (
            <div className="max-h-[75vh] overflow-y-auto p-6">
              <div className="rounded-lg border-2 border-red-300 bg-red-50 p-5" data-testid="final-warning">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 shrink-0 text-red-600" size={26} aria-hidden="true" />
                  <div>
                    <h4 className="text-base font-bold text-red-700">
                      Final warning: this is an Outpost-wide change
                    </h4>
                    <p className="mt-2 text-sm leading-relaxed text-gray-800">
                      This capacity task reconfigures the <strong>entire Outpost</strong>, not a
                      single asset. AWS decides which assets each slotting change lands on.
                      Per-droplet placement is <strong>out of your control</strong> once the
                      task starts.
                    </p>
                    <ul className="mt-2 list-disc pl-5 text-sm leading-relaxed text-gray-800">
                      <li>Every active compute asset on this Outpost can be re-slotted.</li>
                      <li>
                        Running instances that block the new layout will be handled per your
                        selection: <strong>{taskAction}</strong>.
                      </li>
                      <li>
                        Protected from stopping: <strong>{protectedCount}</strong> selection
                        {protectedCount === 1 ? '' : 's'} (instances, accounts, or services).
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-red-600"
                  data-testid="final-ack"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                I understand this change reconfigures capacity across the entire Outpost and
                that AWS decides where each change is applied.
              </label>

              <div className="mt-6 flex justify-end gap-3">
                <Button
                  variant="secondary"
                  data-testid="final-back"
                  disabled={pending}
                  onClick={() => {
                    setAcknowledged(false);
                    setStep('form');
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="danger"
                  data-testid="final-apply"
                  disabled={!acknowledged || pending}
                  onClick={() => onConfirm(buildOptions())}
                >
                  {pending ? 'Applying…' : 'Apply outpost-wide change'}
                </Button>
              </div>
            </div>
          ) : (
          <div className="max-h-[75vh] overflow-y-auto p-6">
            <DiffTable diff={diff} />

            <fieldset className="mt-4 rounded border border-gray-200 px-4 py-3">
              <legend className="px-2 text-xs uppercase tracking-wide text-gray-500">
                If running instances block this task
              </legend>
              <label className="flex cursor-pointer items-center gap-2 py-1 text-sm text-gray-800">
                <input
                  type="radio"
                  name="task-action"
                  value="FAIL_TASK"
                  className="accent-orange-500"
                  checked={taskAction === 'FAIL_TASK'}
                  onChange={() => setTaskAction('FAIL_TASK')}
                />
                Fail the task (FAIL_TASK)
              </label>
              <label className="flex cursor-pointer items-center gap-2 py-1 text-sm text-gray-800">
                <input
                  type="radio"
                  name="task-action"
                  value="WAIT_FOR_EVACUATION"
                  className="accent-orange-500"
                  data-testid="action-wait"
                  checked={taskAction === 'WAIT_FOR_EVACUATION'}
                  onChange={() => setTaskAction('WAIT_FOR_EVACUATION')}
                />
                Wait for evacuation, re-checking every 10 minutes for 48 hours
                (WAIT_FOR_EVACUATION)
              </label>
            </fieldset>

            <fieldset className="mt-4 rounded border border-gray-200 px-4 py-3">
              <legend className="px-2 text-xs uppercase tracking-wide text-gray-500">
                Instances that must not be stopped (optional)
              </legend>

              {instancesLoading ? (
                <p className="py-3 text-sm text-gray-500">Loading running instances…</p>
              ) : instances === null ? (
                <p className="py-3 text-sm text-gray-500" data-testid="exclude-unavailable">
                  The instance list is unavailable right now, so exclusions cannot be selected.
                </p>
              ) : instances.length === 0 ? (
                <p className="py-3 text-sm text-gray-500" data-testid="exclude-empty">
                  No running instances in this scope, nothing to protect.
                </p>
              ) : (
                <>
                  <p className="mb-2 text-sm text-gray-600">
                    Check the running instances this task must not stop:
                  </p>
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <div className="max-h-56 overflow-y-auto">
                      <table className="w-full bg-white text-xs">
                        <thead className="sticky top-0 bg-gray-200">
                          <tr>
                            <th scope="col" className="w-8 px-3 py-2" aria-label="Select" />
                            <th scope="col" className="px-3 py-2 text-left font-bold uppercase tracking-wider text-gray-700">
                              Instance ID
                            </th>
                            <th scope="col" className="px-3 py-2 text-left font-bold uppercase tracking-wider text-gray-700">
                              Name
                            </th>
                            <th scope="col" className="px-3 py-2 text-left font-bold uppercase tracking-wider text-gray-700">
                              Type
                            </th>
                            <th scope="col" className="px-3 py-2 text-left font-bold uppercase tracking-wider text-gray-700">
                              Account
                            </th>
                            <th scope="col" className="px-3 py-2 text-left font-bold uppercase tracking-wider text-gray-700">
                              Service
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {instances.map((instance) => (
                            <tr
                              key={instance.instanceId}
                              className="cursor-pointer transition-colors duration-150 hover:bg-orange-50"
                              onClick={() =>
                                setExcludedInstances((prev) =>
                                  toggleValue(prev, instance.instanceId),
                                )
                              }
                            >
                              <td className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  className="accent-orange-500"
                                  aria-label={`Do not stop ${instance.instanceId}`}
                                  data-testid={`exclude-instance-${instance.instanceId}`}
                                  checked={excludedInstances.includes(instance.instanceId)}
                                  onChange={() =>
                                    setExcludedInstances((prev) =>
                                      toggleValue(prev, instance.instanceId),
                                    )
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 font-mono text-gray-900">
                                {instance.instanceId}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-gray-900">
                                {instance.name ?? '—'}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-gray-900">
                                {instance.instanceType}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-gray-900">
                                {instance.accountId}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-gray-900">
                                {instance.awsServiceName}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {(accounts.length > 1 || services.length > 1) && (
                    <div className="mt-3 flex flex-col gap-2">
                      {accounts.length > 1 && (
                        <div
                          className="flex flex-wrap items-center gap-3"
                          role="group"
                          aria-label="Accounts to exclude"
                        >
                          <span className="text-xs uppercase tracking-wide text-gray-500">
                            Protect whole accounts:
                          </span>
                          {accounts.map((accountId) => (
                            <label
                              key={accountId}
                              className="flex cursor-pointer items-center gap-2 text-sm text-gray-800"
                            >
                              <input
                                type="checkbox"
                                className="accent-orange-500"
                                data-testid={`exclude-account-${accountId}`}
                                checked={excludedAccounts.includes(accountId)}
                                onChange={() =>
                                  setExcludedAccounts((prev) => toggleValue(prev, accountId))
                                }
                              />
                              {accountId}
                            </label>
                          ))}
                        </div>
                      )}
                      {services.length > 1 && (
                        <div
                          className="flex flex-wrap items-center gap-3"
                          role="group"
                          aria-label="Services to exclude"
                        >
                          <span className="text-xs uppercase tracking-wide text-gray-500">
                            Protect whole services:
                          </span>
                          {services.map((service) => (
                            <label
                              key={service}
                              className="flex cursor-pointer items-center gap-2 text-sm text-gray-800"
                            >
                              <input
                                type="checkbox"
                                className="accent-orange-500"
                                data-testid={`exclude-service-${service}`}
                                checked={excludedServices.includes(service)}
                                onChange={() =>
                                  setExcludedServices((prev) => toggleValue(prev, service))
                                }
                              />
                              {service}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {(excludedInstances.length > 0 ||
                    excludedAccounts.length > 0 ||
                    excludedServices.length > 0) && (
                    <p className="mt-3 text-sm text-gray-600" data-testid="exclude-summary">
                      Protected: {excludedInstances.length} instance
                      {excludedInstances.length === 1 ? '' : 's'}
                      {excludedAccounts.length > 0
                        ? ` · ${excludedAccounts.length} account${excludedAccounts.length === 1 ? '' : 's'}`
                        : ''}
                      {excludedServices.length > 0
                        ? ` · ${excludedServices.length} service${excludedServices.length === 1 ? '' : 's'}`
                        : ''}
                    </p>
                  )}
                </>
              )}
            </fieldset>

            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="secondary"
                data-testid="cancel-apply"
                disabled={pending}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button data-testid="confirm-apply" disabled={pending} onClick={confirm}>
                {pending ? 'Applying…' : 'Confirm apply'}
              </Button>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
